(function () {
  'use strict';

  /**
   * Drives the circular prev/next arrows and pagination dots overlaid on
   * the product gallery. It never re-implements slide switching itself -
   * every click is forwarded to the theme's existing (hidden) gallery
   * thumbnail buttons so all existing zoom/video/model/variant behavior
   * keeps working untouched.
   */
  function initGalleryDotsNav(root) {
    if (root.dataset.galleryDotsNavInitialized === 'true') return;
    root.dataset.galleryDotsNavInitialized = 'true';

    var nav = root.querySelector('[data-gallery-dots-navigation]');
    if (!nav) return;

    var viewer = root.querySelector('[data-gallery-viewer]');
    var thumbnails = Array.prototype.slice.call(
      root.querySelectorAll('[data-gallery-thumbnail]')
    );
    if (!thumbnails.length) return;

    var figures = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-figure]'));
    var dots = Array.prototype.slice.call(nav.querySelectorAll('[data-gallery-dot]'));
    var prevButton = nav.querySelector('[data-gallery-dots-prev]');
    var nextButton = nav.querySelector('[data-gallery-dots-next]');

    function currentIndex() {
      var selected = thumbnails.find(function (thumb) {
        return thumb.getAttribute('data-gallery-selected') === 'true';
      });
      return selected ? parseInt(selected.getAttribute('data-gallery-index'), 10) : 0;
    }

    function findByIndex(list, index) {
      return list.find(function (el) {
        return parseInt(el.getAttribute('data-gallery-index'), 10) === index;
      });
    }

    // Selects a slide by index. Forwards to the theme's existing (hidden)
    // thumbnail button so zoom/video/model selection state stays correct,
    // and - on the peek layout - scrolls that slide into view.
    function selectIndex(index, scrollIntoView) {
      var count = thumbnails.length;
      var target = ((index % count) + count) % count;
      var thumb = findByIndex(thumbnails, target);
      if (!thumb) return;

      if (thumb.getAttribute('data-gallery-selected') !== 'true') {
        thumb.click();
      }

      if (scrollIntoView !== false && viewer) {
        var figure = findByIndex(figures, target);
        if (figure) {
          // .product-gallery--viewer is position:relative and figures are
          // its direct children, so it is their offsetParent - offsetLeft
          // is already relative to the viewer itself.
          viewer.scrollTo({
            left: figure.offsetLeft,
            behavior: 'smooth',
          });
        }
      }
    }

    function syncActiveDot() {
      var active = currentIndex();
      dots.forEach(function (dot) {
        var isActive = parseInt(dot.getAttribute('data-gallery-index'), 10) === active;
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        selectIndex(parseInt(dot.getAttribute('data-gallery-index'), 10));
      });
    });

    // Desktop shows exactly two full slides at a time (no peek) - the
    // arrows step by a full pair there, so "next" swaps in a completely
    // new pair of images rather than sliding the window by one. Mobile
    // is untouched (single-slide crossfade), so it still steps by 1.
    function navStep() {
      return window.matchMedia('(min-width: 720px)').matches ? 2 : 1;
    }

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        selectIndex(currentIndex() - navStep());
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        selectIndex(currentIndex() + navStep());
      });
    }

    // On the peek layout, several slides are visible at once. Clicking a
    // slide that isn't the selected one brings it into focus instead of
    // immediately handing the click to the theme's click-to-zoom handler
    // (which always targets the currently selected slide).
    figures.forEach(function (figure) {
      figure.addEventListener('click', function (event) {
        if (figure.getAttribute('data-gallery-selected') !== 'true') {
          event.stopPropagation();
          selectIndex(parseInt(figure.getAttribute('data-gallery-index'), 10));
        }
      });
    });

    // Keep selection/dots in sync when the shopper scrolls/swipes the peek
    // row directly instead of using the dots or arrows.
    if (viewer) {
      var scrollSyncTimeout;
      viewer.addEventListener('scroll', function () {
        clearTimeout(scrollSyncTimeout);
        scrollSyncTimeout = setTimeout(function () {
          var viewerLeft = viewer.getBoundingClientRect().left;
          var closest = null;
          var closestDistance = Infinity;

          figures.forEach(function (figure) {
            var distance = Math.abs(figure.getBoundingClientRect().left - viewerLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              closest = figure;
            }
          });

          if (closest) {
            selectIndex(parseInt(closest.getAttribute('data-gallery-index'), 10), false);
          }
        }, 120);
      });
    }

    var navigationContainer = root.querySelector('[data-gallery-navigation]');
    if (navigationContainer && 'MutationObserver' in window) {
      var observer = new MutationObserver(syncActiveDot);
      observer.observe(navigationContainer, {
        attributes: true,
        attributeFilter: ['data-gallery-selected'],
        subtree: true,
      });
    }

    syncActiveDot();
  }

  function initAll() {
    document.querySelectorAll('[data-product-gallery]').forEach(initGalleryDotsNav);
  }

  function initFavouriteButtons() {
    document.querySelectorAll('[data-product-favourite]').forEach(function (button) {
      if (button.dataset.favouriteInitialized === 'true') return;
      button.dataset.favouriteInitialized = 'true';

      var productId = button.getAttribute('data-product-favourite-id');
      var storageKey = 'theme:favourites';

      function getFavourites() {
        try {
          return JSON.parse(window.localStorage.getItem(storageKey)) || [];
        } catch (error) {
          return [];
        }
      }

      function isFavourited() {
        return getFavourites().indexOf(productId) !== -1;
      }

      function render() {
        var active = isFavourited();
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      }

      button.addEventListener('click', function () {
        var favourites = getFavourites();
        var index = favourites.indexOf(productId);

        if (index === -1) {
          favourites.push(productId);
        } else {
          favourites.splice(index, 1);
        }

        try {
          window.localStorage.setItem(storageKey, JSON.stringify(favourites));
        } catch (error) {
          // Storage unavailable (private browsing, quota, etc.) - button
          // still toggles visually for this page view.
        }

        render();
      });

      render();
    });
  }

  /**
   * Single "Share" trigger button. Uses the native Web Share API (the OS
   * share sheet) where supported - the same behavior as clicking any of
   * the existing Facebook/X/LinkedIn/Pinterest links, just handed to the
   * platform instead. Where navigator.share isn't available, reveals the
   * existing share-buttons markup (untouched, same hrefs as before) as a
   * popover instead of replacing it.
   */
  function initShareTriggers() {
    document.querySelectorAll('[data-product-share-trigger]').forEach(function (trigger) {
      if (trigger.dataset.shareInitialized === 'true') return;
      trigger.dataset.shareInitialized = 'true';

      var wrapper = trigger.closest('[data-product-share-wrapper]');
      var popover = wrapper ? wrapper.querySelector('[data-product-share-popover]') : null;

      function closePopover() {
        if (!popover) return;
        popover.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      }

      trigger.addEventListener('click', function () {
        if (navigator.share) {
          navigator
            .share({
              title: trigger.getAttribute('data-share-title') || document.title,
              url: trigger.getAttribute('data-share-url') || window.location.href,
            })
            .catch(function () {
              // User cancelled the native share sheet, or it failed silently - no fallback needed here.
            });
          return;
        }

        if (!popover) return;
        var isOpen = !popover.hidden;
        popover.hidden = isOpen;
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });

      if (popover) {
        document.addEventListener('click', function (event) {
          if (popover.hidden) return;
          if (wrapper.contains(event.target)) return;
          closePopover();
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') closePopover();
        });
      }
    });
  }

  /**
   * Small click-to-toggle popover for the shipping-info ("i") button -
   * same open/close/outside-click/Escape pattern as the share popover
   * above, just without the native-share branch.
   */
  function initShippingInfoTriggers() {
    document.querySelectorAll('[data-shipping-info-trigger]').forEach(function (trigger) {
      if (trigger.dataset.shippingInfoInitialized === 'true') return;
      trigger.dataset.shippingInfoInitialized = 'true';

      var wrapper = trigger.closest('[data-shipping-info-wrapper]');
      var popover = wrapper ? wrapper.querySelector('[data-shipping-info-popover]') : null;
      if (!popover) return;

      var closeButton = popover.querySelector('[data-shipping-info-close]');

      // Move the popover out of the sticky info column (.product-main uses
      // overflow-y:auto to clamp its height while sticky, which also forces
      // overflow-x to 'auto' on that element - so anything positioned
      // absolute inside it gets clipped/creates a horizontal scrollbar
      // once it's wider than the column, regardless of z-index). Making it
      // a direct child of <body> and position:fixed sidesteps that.
      document.body.appendChild(popover);

      function positionPopover() {
        var margin = 20;
        var triggerRect = trigger.getBoundingClientRect();
        var popoverWidth = popover.offsetWidth;

        var left = triggerRect.left;
        var maxLeft = window.innerWidth - popoverWidth - margin;
        if (left > maxLeft) left = maxLeft;
        if (left < margin) left = margin;

        var top = triggerRect.bottom + 14;

        popover.style.left = left + 'px';
        popover.style.top = top + 'px';

        // Keep the caret pointing at the icon even after any horizontal
        // clamping above.
        var caretCenter = triggerRect.left + triggerRect.width / 2 - left;
        popover.style.setProperty('--popover-caret-left', caretCenter - 7 + 'px');
      }

      function openPopover() {
        popover.hidden = false;
        positionPopover();
        trigger.setAttribute('aria-expanded', 'true');
      }

      function closePopover() {
        popover.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      }

      trigger.addEventListener('click', function () {
        if (popover.hidden) {
          openPopover();
        } else {
          closePopover();
        }
      });

      if (closeButton) {
        closeButton.addEventListener('click', closePopover);
      }

      document.addEventListener('click', function (event) {
        if (popover.hidden) return;
        if (wrapper.contains(event.target) || popover.contains(event.target)) return;
        closePopover();
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closePopover();
      });
      window.addEventListener('resize', function () {
        if (!popover.hidden) positionPopover();
      });
      window.addEventListener(
        'scroll',
        function () {
          if (!popover.hidden) positionPopover();
        },
        true
      );
    });
  }

  /**
   * Back-in-stock modal, opened from the "Notify me when available" link
   * (see snippets/product-form.liquid). UI/interaction only, three steps:
   *   1. pick an out-of-stock size (custom circular radios)
   *   2. enter an email (+ optional marketing opt-in checkbox)
   *   3. confirmation
   *
   * Nothing is ever submitted anywhere - there is no fetch/form-submit
   * call here by design, since no back-in-stock email service/app/API is
   * wired up yet. When one is, this is the seam to connect it: everything
   * the shopper picked (product id, size, email, marketing opt-in) is
   * already assembled into a plain object right where NOTIFY ME is
   * clicked below (search "submissionPayload") - swap the console.log
   * there for the real request and this flow needs no other changes.
   *
   * The out-of-stock size list is recomputed every time the modal opens,
   * scoped to whichever OTHER option (e.g. color) is currently selected
   * on the page - it reads the theme's own data-variant-option-index /
   * data-variant-option-chosen-value attributes (kept in sync by the
   * existing variant picker), so no separate reactive wiring is needed:
   * change color, reopen the modal, and the list reflects that color.
   */
  function initNotifyModal() {
    // Shared across every modal instance on the page (there's normally
    // just one) so nested open calls can't unlock scrolling too early.
    var scrollLockCount = 0;
    var savedBodyOverflow = '';
    var savedBodyPaddingRight = '';

    function lockBodyScroll() {
      if (scrollLockCount === 0) {
        // Compensates for the scrollbar disappearing so the page doesn't
        // shift width when scrolling is locked.
        var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        savedBodyOverflow = document.body.style.overflow;
        savedBodyPaddingRight = document.body.style.paddingRight;
        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
          var currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
          document.body.style.paddingRight = currentPaddingRight + scrollbarWidth + 'px';
        }
      }
      scrollLockCount++;
    }

    function unlockBodyScroll() {
      scrollLockCount = Math.max(0, scrollLockCount - 1);
      if (scrollLockCount === 0) {
        document.body.style.overflow = savedBodyOverflow;
        document.body.style.paddingRight = savedBodyPaddingRight;
      }
    }

    document.querySelectorAll('[data-notify-modal-target]').forEach(function (trigger) {
      if (trigger.dataset.notifyModalInitialized === 'true') return;
      trigger.dataset.notifyModalInitialized = 'true';

      var modal = document.getElementById(trigger.getAttribute('data-notify-modal-target'));
      if (!modal) return;

      // Move the modal out of the sticky product-info column and make it
      // a direct child of <body>. .product-main uses position:sticky,
      // which - like any positioned/sticky element - creates its own
      // stacking context; a position:fixed modal left nested inside it
      // still lays out relative to the viewport, but its z-index would
      // only be compared *within* that stacking context, so page
      // elements outside it (header, gallery nav, sticky cart, etc.)
      // could still paint above it regardless of z-index. Re-parenting
      // to <body> guarantees the modal is centered on the true viewport
      // and stacks above everything else on the page, independent of
      // wherever its trigger link happens to live (same fix already used
      // for the shipping-info popover above).
      document.body.appendChild(modal);

      var panel = modal.querySelector('.notify-modal__panel');
      var closeButton = modal.querySelector('[data-notify-modal-close]');
      var overlay = modal.querySelector('[data-notify-modal-overlay]');
      var nextButton = modal.querySelector('[data-notify-modal-next]');
      var submitButton = modal.querySelector('[data-notify-modal-submit]');
      var doneButton = modal.querySelector('[data-notify-modal-done]');
      var steps = {
        1: modal.querySelector('[data-notify-modal-step="1"]'),
        2: modal.querySelector('[data-notify-modal-step="2"]'),
        3: modal.querySelector('[data-notify-modal-step="3"]'),
      };
      var sizesContainer = modal.querySelector('[data-notify-modal-sizes]');
      var emailInput = modal.querySelector('[data-notify-modal-email]');
      var errorMessage = modal.querySelector('[data-notify-modal-error]');
      var marketingCheckbox = modal.querySelector('[data-notify-modal-marketing]');
      var confirmationText = modal.querySelector('[data-notify-modal-confirmation]');
      var dataScript = modal.querySelector('[data-notify-modal-variants]');
      var productId = modal.getAttribute('data-notify-modal-product') || '';

      var variantData = null;
      if (dataScript) {
        try {
          variantData = JSON.parse(dataScript.textContent);
        } catch (error) {
          variantData = null;
        }
      }

      function showStep(number) {
        Object.keys(steps).forEach(function (key) {
          var step = steps[key];
          if (step) step.hidden = Number(key) !== number;
        });
      }

      // Reads the currently chosen value for a given option index from
      // the theme's own variant-picker markup (radios or a <select>, both
      // carry this same pair of attributes on their shared wrapper) -
      // "false" is that markup's own convention for "nothing chosen yet".
      function getChosenOptionValue(optionIndex) {
        var el = document.querySelector('[data-variant-option-index="' + optionIndex + '"]');
        if (!el) return null;
        var value = el.getAttribute('data-variant-option-chosen-value');
        if (!value || value === 'false') return null;
        return value;
      }

      function computeOutOfStockSizes() {
        if (!variantData || !variantData.sizeValues || !variantData.sizeValues.length) {
          return { values: [], showAll: false };
        }

        var otherIndexes = variantData.otherIndexes || [];
        var currentOthers = otherIndexes.map(getChosenOptionValue);

        function matchesCurrentOthers(variant) {
          return otherIndexes.every(function (optionIndex, i) {
            var current = currentOthers[i];
            if (current === null) return true; // not chosen yet - don't filter on it
            return (variant.o && variant.o[i]) === current;
          });
        }

        var outOfStock = variantData.sizeValues.filter(function (size) {
          var hasAvailableMatch = (variantData.variants || []).some(function (variant) {
            return variant.s === size && variant.a && matchesCurrentOthers(variant);
          });
          return !hasAvailableMatch;
        });

        if (outOfStock.length === 0) {
          return { values: variantData.sizeValues, showAll: true };
        }
        return { values: outOfStock, showAll: false };
      }

      // The specific variant id for a chosen size (matched against
      // whichever other option, e.g. color, is currently selected on the
      // page) - this is the id a real back-in-stock service would need to
      // know exactly which variant to watch and notify on.
      function findVariantId(size) {
        if (!variantData) return null;
        var otherIndexes = variantData.otherIndexes || [];
        var currentOthers = otherIndexes.map(getChosenOptionValue);
        var match = (variantData.variants || []).find(function (variant) {
          if (variant.s !== size) return false;
          return otherIndexes.every(function (optionIndex, i) {
            var current = currentOthers[i];
            if (current === null) return true;
            return (variant.o && variant.o[i]) === current;
          });
        });
        return match ? match.id : null;
      }

      function renderSizes() {
        if (!sizesContainer) return;
        var result = computeOutOfStockSizes();

        sizesContainer.innerHTML = '';
        result.values.forEach(function (size, index) {
          var label = document.createElement('label');
          label.className = 'notify-modal__size-option';

          var input = document.createElement('input');
          input.type = 'radio';
          input.name = 'notify-size-' + (trigger.getAttribute('data-notify-modal-target') || '');
          input.value = size;
          input.className = 'notify-modal__size-radio';
          input.setAttribute('data-notify-modal-size-radio', '');
          if (index === 0) input.checked = true;

          var visual = document.createElement('span');
          visual.className = 'notify-modal__size-radio-visual';
          visual.setAttribute('aria-hidden', 'true');

          var text = document.createElement('span');
          text.className = 'notify-modal__size-label';
          text.textContent = size;

          label.appendChild(input);
          label.appendChild(visual);
          label.appendChild(text);
          sizesContainer.appendChild(label);
        });

        // Disabled only in the edge case where the product has no
        // options at all to build a size list from - a size is always
        // pre-selected above otherwise, so NEXT stays enabled from the
        // start (matching the reference, where the first size ships
        // pre-selected too).
        if (nextButton) nextButton.disabled = result.values.length === 0;
      }

      function showError(message) {
        if (errorMessage) {
          if (message) errorMessage.textContent = message;
          errorMessage.classList.add('is-visible');
        }
        if (emailInput) emailInput.classList.add('has-error');
      }

      function clearError() {
        if (errorMessage) errorMessage.classList.remove('is-visible');
        if (emailInput) emailInput.classList.remove('has-error');
      }

      if (emailInput) {
        emailInput.addEventListener('input', clearError);
      }

      function resetModal() {
        showStep(1);
        renderSizes();
        clearError();
        if (emailInput) emailInput.value = '';
        if (marketingCheckbox) marketingCheckbox.checked = false;
      }

      function openModal() {
        renderSizes();
        modal.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        lockBodyScroll();
        if (closeButton) closeButton.focus();
      }

      function closeModal() {
        if (modal.hidden) return;
        modal.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        unlockBodyScroll();
        resetModal();
      }

      trigger.addEventListener('click', openModal);
      if (closeButton) closeButton.addEventListener('click', closeModal);
      if (overlay) overlay.addEventListener('click', closeModal);

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !modal.hidden) closeModal();
      });

      // Clicking inside the panel shouldn't bubble to the overlay, which
      // otherwise sits directly behind it and would close the modal.
      if (panel) {
        panel.addEventListener('click', function (event) {
          event.stopPropagation();
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', function () {
          showStep(2);
          if (emailInput) emailInput.focus();
        });
      }

      if (submitButton) {
        submitButton.addEventListener('click', function () {
          // Custom in-modal message instead of the browser's own
          // validation tooltip, which can render outside/overlapping the
          // modal - checkValidity() still does the actual required/
          // type=email format check, just without popping its own UI.
          if (emailInput && !emailInput.checkValidity()) {
            showError();
            emailInput.focus();
            return;
          }
          clearError();

          var selectedSize = modal.querySelector('[data-notify-modal-size-radio]:checked');

          // Everything a real back-in-stock service/app would need:
          // product, the specific variant (matched to the chosen size +
          // whatever other option, e.g. color, is currently selected),
          // the size label itself, the email, and marketing consent.
          // Not sent anywhere yet - see the function's doc comment above.
          var submissionPayload = {
            productId: productId,
            variantId: selectedSize ? findVariantId(selectedSize.value) : null,
            size: selectedSize ? selectedSize.value : null,
            email: emailInput ? emailInput.value : '',
            marketingOptIn: marketingCheckbox ? marketingCheckbox.checked : false,
          };
          // eslint-disable-next-line no-console
          console.log('[notify-modal] ready to submit (no backend connected yet):', submissionPayload);

          if (confirmationText) {
            var template = confirmationText.getAttribute('data-notify-modal-confirmation-template') || confirmationText.textContent;
            confirmationText.textContent = selectedSize ? template.replace('[size]', selectedSize.value) : template;
          }

          showStep(3);
        });
      }

      if (doneButton) {
        doneButton.addEventListener('click', closeModal);
      }
    });
  }

  /**
   * Product-information accordion below the shipping/stock row. Unlike
   * the below-gallery tabs (which allow several open at once), opening
   * one item here closes any other open item, matching the reference's
   * classic single-open accordion behavior.
   */
  function initProductInfoAccordion() {
    var triggers = Array.prototype.slice.call(
      document.querySelectorAll('[data-product-accordion-trigger]')
    );
    if (!triggers.length) return;

    triggers.forEach(function (trigger) {
      if (trigger.dataset.accordionInitialized === 'true') return;
      trigger.dataset.accordionInitialized = 'true';

      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';

        triggers.forEach(function (other) {
          other.setAttribute('aria-expanded', 'false');
        });

        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });
  }

  /**
   * Product info accordion (Details/Specifications/Features/Shipping &
   * Return Info, or whatever accordion_item blocks the merchant has),
   * rendered as a normal in-flow block right below the gallery, inside
   * the same left column. Single-open-at-a-time, same pattern as
   * initProductInfoAccordion above but scoped to its own data attributes
   * since it's a fully separate accordion.
   */
  function initProductInfoList() {
    document.querySelectorAll('[data-pia-list]').forEach(function (list) {
      if (list.dataset.piaInitialized === 'true') return;
      list.dataset.piaInitialized = 'true';

      var items = Array.prototype.slice.call(list.querySelectorAll('[data-pia-item]'));

      function setOpen(item, open) {
        var trigger = item.querySelector('[data-pia-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      items.forEach(function (item) {
        var trigger = item.querySelector('[data-pia-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', function () {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';
          items.forEach(function (other) {
            setOpen(other, false);
          });
          setOpen(item, !isOpen);
        });
      });

      list.addEventListener('shopify:block:select', function (event) {
        var item = event.target.closest('[data-pia-item]');
        if (item) setOpen(item, true);
      });
    });
  }

  /**
   * The displayed product title must always stay the clean base product
   * title and never pick up appended variant info (size/color), including
   * from third-party variant/swatch apps that aren't part of this theme's
   * own code and can update the DOM directly after variant selection.
   * Confirmed live: an installed app ("Variant Title King",
   * cdn.shopify.com/extensions/.../variant-title-king.min.js) does exactly
   * this ~1-1.5s after load, via `.product-title` heading element .innerText
   * = "...", which replaces ALL of that element's children in one go.
   *
   * data-product-title-guard/data-clean-title live on the heading element
   * itself (h1/h2 in snippets/product.liquid) for that reason, not on an
   * inner <span> - an .innerText write like that destroys any inner span
   * outright, and a MutationObserver watching only that span never even
   * fires: the mutation removing it happens on the H1's own childList, one
   * level above what a span-scoped observer would be watching. The H1/H2
   * itself is never replaced (only its children), so observing it directly
   * survives the attack. (One exception: the quickshop-modal/featured-
   * product title still guards its inner <span>, since that heading also
   * wraps a real <a> link this restoration must not clobber - a narrower,
   * lower-risk context not confirmed to be targeted by this app.)
   */
  function initProductTitleGuard() {
    document.querySelectorAll('[data-product-title-guard]').forEach(function (el) {
      if (el.dataset.titleGuardInitialized === 'true') return;
      el.dataset.titleGuardInitialized = 'true';

      var cleanTitle = el.getAttribute('data-clean-title') || el.textContent;

      var observer = new MutationObserver(function () {
        if (el.textContent !== cleanTitle) {
          observer.disconnect();
          el.textContent = cleanTitle;
          observer.observe(el, { childList: true, characterData: true, subtree: true });
        }
      });
      observer.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  /**
   * Size button grid: keeps sold-out sizes truly unselectable (native
   * .disabled, not just CSS) without touching the shared sold_out_options
   * theme setting, since that setting is global and would also disable
   * sold-out Color swatches - out of scope for this change. Also keeps
   * the "There is N units left" message under the grid in sync with the
   * selected variant's live inventory_quantity, reacting to the
   * variant-change event assets/empire.js dispatches on <variant-selection>
   * for every option change (Size or Color). The initial paint already
   * comes correct from options-radios.liquid's own server-rendered
   * selected_variant markup; this only has to keep it correct afterward.
   */
  function initSizeSelector() {
    document.querySelectorAll('[data-variant-selection]').forEach(function (variantSelection) {
      var fieldset = variantSelection.querySelector('.options-selection__radios');
      if (!fieldset) return;
      if (fieldset.dataset.sizeSelectorInitialized === 'true') return;
      fieldset.dataset.sizeSelectorInitialized = 'true';

      var stockMessage = fieldset.querySelector('[data-variant-stock-message]');
      var stockText = stockMessage ? stockMessage.querySelector('[data-variant-stock-text]') : null;

      var inventoryMap = {};
      var inventoryMapEl = fieldset.querySelector('[data-variant-inventory-map]');
      if (inventoryMapEl) {
        try {
          inventoryMap = JSON.parse(inventoryMapEl.textContent) || {};
        } catch (e) {
          inventoryMap = {};
        }
      }

      function syncAvailability() {
        fieldset.querySelectorAll('.options-selection__option-value-input').forEach(function (input) {
          input.disabled = input.dataset.variantOptionAvailable === 'false';
        });
      }

      function syncStockMessage(variant) {
        if (!stockMessage || !stockText) return;

        // Shopify's public /products/{handle}.js endpoint (what
        // <variant-selection> fetches) omits inventory_quantity and
        // inventory_policy, so the real numbers come from
        // data-variant-inventory-map (server-rendered) instead, looked
        // up by the variant id this event does carry.
        var inventory = variant ? inventoryMap[String(variant.id)] : null;

        var tier = 'none';
        var qty = 0;

        if (inventory && inventory.inventory_management && inventory.inventory_policy !== 'continue') {
          qty = inventory.inventory_quantity;
          if (qty >= 1 && qty <= 3) {
            tier = 'urgent';
          } else if (qty >= 4 && qty <= 9) {
            tier = 'moderate';
          }
        }

        stockMessage.classList.remove(
          'options-selection__stock-message--urgent',
          'options-selection__stock-message--moderate',
          'options-selection__stock-message--none'
        );
        stockMessage.classList.add('options-selection__stock-message--' + tier);

        if (tier === 'none') {
          stockMessage.hidden = true;
          return;
        }

        var template = qty === 1
          ? stockMessage.getAttribute('data-stock-singular-template')
          : stockMessage.getAttribute('data-stock-plural-template');
        stockText.textContent = template ? template.replace('%%COUNT%%', qty) : '';
        stockMessage.hidden = false;
      }

      syncAvailability();

      variantSelection.addEventListener('variant-change', function (event) {
        syncAvailability();
        syncStockMessage(event.detail ? event.detail.variant : null);
      });
    });
  }

  /**
   * Hides the site header while the product image lightbox (PhotoSwipe,
   * vendored inside assets/empire.js, root element .pswp in
   * layout/theme.liquid) is open. Deliberately doesn't touch empire.js
   * or hook PhotoSwipe's own open/close call sites directly - it's a
   * third-party library bundled into a 30,000+ line vendor file, and
   * there are several independent ways to close it (the close button,
   * Escape, tapping the background) that all converge on the library's
   * own internal close(). Watching .pswp's own class attribute for
   * .pswp--open (PhotoSwipe's own show/hide toggle, display:none <->
   * block in theme.css.liquid) means this always reflects the real
   * state regardless of which of those paths triggered it, with zero
   * dependency on the library's internals. header-redesign.css hides
   * .site-header--redesign whenever body carries pswp-lightbox-active.
   */
  function initLightboxHeaderHide() {
    var pswp = document.querySelector('[data-photoswipe]');
    if (!pswp) return;
    if (pswp.dataset.lightboxHeaderHideInitialized === 'true') return;
    pswp.dataset.lightboxHeaderHideInitialized = 'true';

    function sync() {
      document.body.classList.toggle('pswp-lightbox-active', pswp.classList.contains('pswp--open'));
    }

    new MutationObserver(sync).observe(pswp, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  /**
   * Sets --header-height on :root to the sticky header's actual
   * rendered height, so assets/product-redesign.css's sticky gallery
   * and info columns (".product--outer--sticky-info .product-gallery-
   * column" / ".product--outer--sticky-info .product-main") can offset
   * their `top` correctly and never tuck underneath the header. The
   * header's own --hdr-height custom property
   * (sections/static-header.liquid) is set inline on the <header>
   * element itself, so it never reaches the product columns through
   * normal CSS inheritance - measuring the real element and republishing
   * the value on :root (which every element on the page can read) is
   * what actually makes it usable here, and keeps it correct across
   * breakpoints/font-loading reflows without hardcoding a pixel value
   * tied to the current 72px header-height setting. Runs regardless of
   * whether the sticky gallery is active on this page - a harmless,
   * cheap measurement either way - since it's simpler and more robust
   * than trying to only run it conditionally.
   */
  function initStickyGalleryOffset() {
    var header = document.querySelector('.site-header--redesign');
    if (!header) return;
    if (header.dataset.stickyOffsetInitialized === 'true') return;
    header.dataset.stickyOffsetInitialized = 'true';

    function updateOffset() {
      var height = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', height + 'px');
    }

    updateOffset();
    window.addEventListener('resize', updateOffset);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      initFavouriteButtons();
      initShareTriggers();
      initShippingInfoTriggers();
      initNotifyModal();
      initProductInfoAccordion();
      initProductInfoList();
      initProductTitleGuard();
      initSizeSelector();
      initLightboxHeaderHide();
      initStickyGalleryOffset();
    });
  } else {
    initAll();
    initFavouriteButtons();
    initShareTriggers();
    initShippingInfoTriggers();
    initNotifyModal();
    initProductInfoAccordion();
    initProductInfoList();
    initProductTitleGuard();
    initSizeSelector();
    initLightboxHeaderHide();
    initStickyGalleryOffset();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    section.querySelectorAll('[data-product-gallery]').forEach(initGalleryDotsNav);
    initFavouriteButtons();
    initShareTriggers();
    initShippingInfoTriggers();
    initNotifyModal();
    initProductInfoAccordion();
    initProductInfoList();
    initProductTitleGuard();
    initSizeSelector();
    initLightboxHeaderHide();
    initStickyGalleryOffset();
  });
})();
